import { BrazeImageCard } from '@/components/braze/BrazeImageCard';
import CardButton from '@/components/buttons/CardButton';
import OrderMedicationCard from '@/components/card/OrderMedicationCard';
import PageHeading from '@/components/common/PageHeading';
import { MainContainer } from '@/components/layout/LayoutStyledComponents';
import PatientReferralView from '@/components/patientReferral/patientReferralView';
import {
  FF_BRAZE_CONTENT_CARDS,
  FF_ENABLE_WOM_CAMPAIGN,
  FF_EXPERIMENT_SWAPPING_UMEDS_CARD_ON_HOMEPAGE,
} from '@/constants/featureFlags';
import { PatientConsentContext } from '@/context/PatientConsentProvider';
import useGetConsultations from '@/hooks/consultations/useGetConsultations';
import useGetGreeting from '@/hooks/patient/useGetGreeting';
import useGetPatientOrderSummary from '@/hooks/patient/useGetPatientOrderSummary';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import useCustomer from '@/hooks/user/useCustomer';
import {
  CONSULTATIONS_ROUTE,
  MEDICINE_INFORMATION_ROUTE,
  PATIENT_ORDERS_ROUTE,
  PATIENT_REFILL_ROUTE,
} from '@/routes/route-paths';
import useUser from '@montugroup/circuit-shared/hooks/user/useUser';
import { Box, Stack, Typography } from '@montugroup/design-system';
import { MgPrescription } from '@montugroup/icons';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { Skeleton, styled, useTheme } from '@mui/material';
import { DateTime } from 'luxon';
import { useContext } from 'react';

const LayoutContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(4),
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
  },
}));

export default function PatientHome() {
  const { data: user } = useUser();
  const theme = useTheme();

  const { flags } = useFeatureFlags();

  const { data: customer } = useCustomer();
  const isDischarged = customer?.isDischarged;

  const greeting = useGetGreeting();
  const { data: orderSummary, isLoading } = useGetPatientOrderSummary();

  const { requiresConsentSubmission, isLoadingConsentStatus } = useContext(PatientConsentContext);
  const showStartOrderCard = !isLoadingConsentStatus && !requiresConsentSubmission;

  const {
    consultData,
    isLoading: isConsultLoading,
    isSuccess,
  } = useGetConsultations({
    when: 'upcoming',
    page: {
      size: 1,
    },
  });

  const renderConsultationCard = () => {
    if (isConsultLoading) {
      return (
        <Skeleton
          variant="rectangular"
          width="100%"
          height="7.5rem"
          sx={{
            borderRadius: '8px',
          }}
        />
      );
    }

    if (isSuccess && consultData.length > 0) {
      const consultation = consultData[0];
      const startTime = DateTime.fromISO(consultation.startTime).toFormat('hh:mm');
      const endTime = DateTime.fromISO(consultation.endTime).toFormat('hh:mm a');
      const longDate = DateTime.fromISO(consultation.startTime).toFormat('cccc, dd LLLL');
      return (
        <CardButton
          title="Your upcoming consultation"
          subtitle={longDate}
          datetime={`${startTime} - ${endTime}`}
          icon={<CalendarTodayIcon />}
          accentColor="secondary"
          link={CONSULTATIONS_ROUTE}
        />
      );
    }

    return (
      <CardButton
        title="Book and view consultations"
        subtitle="View new consultations and review past sessions here"
        icon={<CalendarTodayIcon />}
        accentColor="secondary"
        link={CONSULTATIONS_ROUTE}
      />
    );
  };

  const orderMedicationCard = isLoading ? (
    <Stack flex={1}>
      <Skeleton
        variant="rectangular"
        width="100%"
        height="100%"
        sx={{
          borderRadius: '8px',
        }}
      />
    </Stack>
  ) : (
    <Stack flex={1}>
      <OrderMedicationCard
        primaryButtonText="Start your order"
        primaryButtonLink={PATIENT_REFILL_ROUTE}
        {...(orderSummary?.hasOrders && {
          secondaryButtonText: 'View orders',
          secondaryButtonLink: PATIENT_ORDERS_ROUTE,
        })}
        showDischargedBanner={isDischarged}
      />
    </Stack>
  );

  return (
    <Stack direction="column" justifyContent="space-between">
      <MainContainer>
        <Box maxWidth="lg" pb={{ xs: 10, md: 0 }}>
          <Typography component="h1">
            {greeting}
            {user?.firstName && `, ${user.firstName}`}
          </Typography>
          <PageHeading
            component="h2"
            fontWeight={500}
            marginTop={theme.spacing(2)}
            marginRight={theme.spacing(10)}
            lineHeight={theme.spacing(10)}
          >
            You&apos;re on your way to better health
          </PageHeading>
          {flags[FF_BRAZE_CONTENT_CARDS] && <BrazeImageCard />}
          <LayoutContainer mt={{ xs: 6, md: 10 }}>
            {flags[FF_EXPERIMENT_SWAPPING_UMEDS_CARD_ON_HOMEPAGE] ? (
              // --- Variant WITH swapped order ---
              <>
                {showStartOrderCard && orderMedicationCard}
                <Stack gap={theme.spacing(4)} flex={1}>
                  <Stack flex={1}>{renderConsultationCard()}</Stack>
                  <Stack flex={1}>
                    <CardButton
                      title="Medicine information"
                      subtitle="Discover details about the products you've been prescribed"
                      icon={<MgPrescription />}
                      accentColor="primary"
                      link={MEDICINE_INFORMATION_ROUTE}
                    />
                  </Stack>
                </Stack>
              </>
            ) : (
              // --- Default layout ---
              <>
                <Stack gap={theme.spacing(4)} flex={1}>
                  <Stack flex={1}>{renderConsultationCard()}</Stack>
                  <Stack flex={1}>
                    <CardButton
                      title="Medicine information"
                      subtitle="Discover details about the products you've been prescribed"
                      icon={<MgPrescription />}
                      accentColor="primary"
                      link={MEDICINE_INFORMATION_ROUTE}
                    />
                  </Stack>
                </Stack>
                {showStartOrderCard && orderMedicationCard}
              </>
            )}
          </LayoutContainer>
        </Box>
      </MainContainer>
      {flags[FF_ENABLE_WOM_CAMPAIGN] && (
        <Box sx={{ position: 'fixed', bottom: 0, left: 0, width: '100%', zIndex: '10' }}>
          <PatientReferralView />
        </Box>
      )}
    </Stack>
  );
}

PatientHome.displayName = 'PatientHome';
